/**
 * Partner-tier tenancy helpers (Partner tier guide §1.2).
 *
 * Every partner owns exactly one hidden "home company" (`partner.home_company_id`)
 * whose members are the partner's own staff. It must never be shown as a company:
 * label it "Direct users", and never print its backend name (`partner-{id}-home`).
 *
 * Two contexts need this mapping:
 *   - platform screens know many partners → pass `{ partnerIndex }` from
 *     `buildPartnerIndex(partners)`;
 *   - partner screens know only their own → pass `{ homeCompanyId, partnerName }`.
 */

export const DIRECT_USERS_LABEL = 'Direct users';
export const PLATFORM_DIRECT_LABEL = 'Platform-direct';

// The backend names a home company `partner-{id}-home`. Used only as a safety
// net when a screen has a company name but no partner list to check against —
// that string must never reach the UI.
const HOME_NAME_RE = /^partner-(\d+)-home$/;
export const looksLikeHomeCompanyName = (name) => HOME_NAME_RE.test(String(name || ''));

/** Index a partner list by id and by home-company id. */
export const buildPartnerIndex = (partners = []) => {
  const byId = new Map();
  const byHomeCompanyId = new Map();
  partners.forEach((p) => {
    if (!p) return;
    byId.set(Number(p.id), p);
    if (p.home_company_id != null) byHomeCompanyId.set(Number(p.home_company_id), p);
  });
  return { byId, byHomeCompanyId };
};

export const EMPTY_PARTNER_INDEX = buildPartnerIndex([]);

/** The partner whose home company this is, or null. Platform context. */
export const homePartnerOf = (companyId, partnerIndex) => {
  if (companyId == null || !partnerIndex) return null;
  return partnerIndex.byHomeCompanyId.get(Number(companyId)) || null;
};

/**
 * Is `companyId` a partner home company?
 * @param {number|string} companyId
 * @param {{ partnerIndex?: object, homeCompanyId?: number, name?: string }} ctx
 */
export const isHomeCompany = (companyId, ctx = {}) => {
  if (companyId == null) return false;
  if (ctx.homeCompanyId != null) return Number(companyId) === Number(ctx.homeCompanyId);
  return !!homePartnerOf(companyId, ctx.partnerIndex) || looksLikeHomeCompanyName(ctx.name);
};

/**
 * Display name for a company, mapping a home company to its "direct users" label.
 *   platform ctx → "Acme Consulting · direct users"
 *   partner ctx  → "Direct users"
 * @param {number|string|null} companyId
 * @param {string|null} companyName  backend name (ignored for home companies)
 * @param {{ partnerIndex?: object, homeCompanyId?: number, partnerName?: string }} ctx
 */
export const companyLabel = (companyId, companyName, ctx = {}) => {
  if (ctx.homeCompanyId != null && Number(companyId) === Number(ctx.homeCompanyId)) {
    return DIRECT_USERS_LABEL;
  }
  const partner = homePartnerOf(companyId, ctx.partnerIndex);
  if (partner) return `${partner.name} · direct users`;
  if (looksLikeHomeCompanyName(companyName)) {
    return ctx.homeCompanyId != null ? DIRECT_USERS_LABEL : 'Partner · direct users';
  }
  if (companyName) return companyName;
  return companyId != null ? `#${companyId}` : '—';
};
